import DiscoverabilityAccordion from '@components/common/DiscoverabilityAccordion'
import { EditorGrid, EditorSectionHeading } from '@components/common/EditorWorkspace'
import PollDesign from '@components/common/PollDesign'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { useEffect, useMemo, useState } from 'react'
import { GlassButton, GlassCard, GlassOutlineButton } from '@components/common/Glass'
import { createPoll, getPollCategories, getPollTags, QUESTION_EXPERIENCE_VERSION } from 'src/api/polls'
import { createTopic } from 'src/api/openai'
import { PollItem, PollVisibility, PollState } from 'src/api/types/polls'
import { err, split } from 'src/utils/functions'
import { AlertInput } from '@components/common/Alert'
import Tooltip from '@mui/material/Tooltip'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import {
  altColor,
  altColorDarkText,
  helperTextColor,
  mainColor,
  mainColorDarker,
  mainColorDarkText,
  shadow1,
} from 'src/ui/theme/tokens'
import CircularProgress from '@mui/material/CircularProgress'
import DrawRoundedIcon from '@mui/icons-material/DrawRounded'
import SvgIcon from '@mui/icons-material/DrawRounded'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { AnimatePresence, motion } from 'framer-motion'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
// import IconButton from '@mui/material/IconButton'
// import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'

const MIN_POLL_OPTIONS = 10
const MAX_POLL_OPTIONS = 20
const MAX_AI_POLL_OPTIONS = 20
const POLL_OPTION_CHOICES = [10, 15, 20] as const
const toOptionChoice = (value: number): (typeof POLL_OPTION_CHOICES)[number] => {
  if (value <= 10) return 10
  if (value <= 15) return 15
  return 20
}

export default function PollCreateSection({
  onBack,
  onCreated,
  showHeader = true,
  sounds,
  onAlert,
  initialDraft,
  onConvertToTrivia,
}: {
  onBack?: () => void
  onCreated: (pollId: number, pollFormat?: 'ranked_options' | 'community_answers') => void
  showHeader?: boolean
  sounds?: { click?: () => void; swipe?: () => void }
  onAlert?: (value: AlertInput) => void
  initialDraft?: {
    prompt: string
    items: string[]
    categories?: string[]
    tags?: string[]
    visibility?: PollVisibility
  }
  onConvertToTrivia?: (payload: {
    prompt: string
    items: string[]
    categories: string[]
    tags: string[]
    visibility: 'public' | 'private'
  }) => void
}): JSX.Element {
  const [prompt, setPrompt] = useState('')
  const [pollFormat, setPollFormat] = useState<'ranked_options' | 'community_answers'>(
    'ranked_options'
  )
  const [showSubmittedAnswers, setShowSubmittedAnswers] = useState(true)
  const isQuestion = pollFormat === 'community_answers'
  const [visibility, setVisibility] = useState<PollVisibility>('public')
  const [pollResultsVisibility, setPollResultsVisibility] = useState<'hidden' | 'visible'>(
    'visible'
  )
  const [resultsMode, setResultsMode] = useState<'open' | 'at' | 'when'>('open')
  const [closeOnCount, setCloseOnCount] = useState('')
  const [closeAt, setCloseAt] = useState('')
  const [items, setItems] = useState<string[]>(Array.from({ length: MIN_POLL_OPTIONS }, () => ''))
  const [generateOptionsCount, setGenerateOptionsCount] = useState<number>(MIN_POLL_OPTIONS)
  const [pollCategoryOptions, setPollCategoryOptions] = useState<string[]>([])
  const [pollTagOptions, setPollTagOptions] = useState<string[]>([])
  const [pollCategories, setPollCategories] = useState<string[]>([])
  const [pollTags, setPollTags] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [createMethod, setCreateMethod] = useState<'prompt' | 'manual'>('manual')
  const [forcedPrivateByModeration, setForcedPrivateByModeration] = useState(false)
  const isBusy = loading || aiLoading
  const [itemsAnimKey, setItemsAnimKey] = useState(0)
  const [animateItemsIntro, setAnimateItemsIntro] = useState(false)
  const [suggestTrivia, setSuggestTrivia] = useState(false)
  const [triviaSuggestionDismissed, setTriviaSuggestionDismissed] = useState(false)

  const parseItems = (): PollItem[] => {
    return items
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, MAX_POLL_OPTIONS)
      .map((title, idx) => ({
        title,
        index: idx + 1,
      }))
  }
  const nonEmptyItemCount = items.filter((item) => item.trim()).length
  const hasValidItemCount =
    isQuestion || (nonEmptyItemCount >= MIN_POLL_OPTIONS && nonEmptyItemCount <= MAX_POLL_OPTIONS)

  const setItemsLength = (nextLength: number) => {
    setAnimateItemsIntro(false)
    const clamped = Math.min(MAX_POLL_OPTIONS, Math.max(MIN_POLL_OPTIONS, nextLength))
    setItems((prev) => {
      if (prev.length === clamped) return prev
      if (prev.length > clamped) return prev.slice(0, clamped)
      return prev.concat(Array.from({ length: clamped - prev.length }, () => ''))
    })
    setGenerateOptionsCount(toOptionChoice(clamped))
  }

  const itemsPreview = useMemo(() => parseItems().length, [items])
  const mergedPollCategoryOptions = useMemo(
    () => Array.from(new Set([...(pollCategoryOptions || []), ...pollCategories])).filter(Boolean),
    [pollCategoryOptions, pollCategories]
  )
  const mergedPollTagOptions = useMemo(
    () => Array.from(new Set([...(pollTagOptions || []), ...pollTags])).filter(Boolean),
    [pollTagOptions, pollTags]
  )
  const normalizeMetaValue = (value: string) => value.trim().slice(0, 16)
  const normalizeMetaList = (value: string[]) =>
    value.map(normalizeMetaValue).filter(Boolean).slice(0, 3)
  const filterOption = useMemo(() => createFilterOptions<string>(), [])
  const inputSx = {
    background: '#ffffff',
    color: '#693413',
    borderRadius: '14px',
    '& fieldset': {
      borderColor: '#e8cdb2',
    },
    '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': { borderColor: '#e98549' },
    '&.Mui-focused fieldset': { borderColor: '#e98549' },
    '& input::placeholder': { color: '#855c3d', opacity: 0.8 },
    '&.Mui-disabled': {
      background: '#f7e9da',
      '& fieldset': { borderColor: '#ead5c0' },
      '& input, & .MuiSelect-select': { WebkitTextFillColor: '#987457' },
    },
  } as const
  const pollMenuPaperSx = {
    background: '#ffffff',
    color: '#693413',
    border: '1px solid #efddc9',
    '& .MuiMenuItem-root.Mui-selected, & .MuiAutocomplete-option[aria-selected="true"]': {
      background: '#ffdfc2',
    },
    '& .MuiMenuItem-root:hover, & .MuiAutocomplete-option.Mui-focused': {
      background: '#ffefdd',
    },
  }

  const handleCreate = () => {
    const items = isQuestion ? [] : parseItems()
    if (!prompt.trim()) return
    if (
      !isQuestion &&
      (!hasValidItemCount || items.length < MIN_POLL_OPTIONS || items.length > MAX_POLL_OPTIONS)
    ) {
      onAlert?.({
        open: true,
        severity: 'warning',
        message: `Polls must have between ${MIN_POLL_OPTIONS} and ${MAX_POLL_OPTIONS} items.`,
      })
      return
    }
    const state: PollState = resultsMode === 'open' ? 'results_open' : 'collecting'
    setLoading(true)
    createPoll({
      poll: {
        pollFormat,
        ...(isQuestion ? { questionExperienceVersion: QUESTION_EXPERIENCE_VERSION } : {}),
        showSubmittedAnswers,
        prompt,
        visibility,
        createMethod,
        state,
        category: pollCategories.join(','),
        tags: pollTags.join(','),
        pollResultsVisibility: visibility === 'private' ? pollResultsVisibility : 'visible',
        closeOnCount: resultsMode === 'when' ? parseInt(closeOnCount) : undefined,
        closeAt: resultsMode === 'at' && closeAt ? new Date(closeAt).toISOString() : undefined,
      },
      items,
    })
      .then((res) => {
        setLoading(false)
        if (res.success && res.data?.id) {
          onCreated(res.data.id, res.data.pollFormat)
          return
        }
        let message = res.message || 'Failed to create poll.'
        try {
          const parsed = JSON.parse(message)
          message = parsed?.error?.message || parsed?.message || message
        } catch {
          // keep original
        }
        onAlert?.({
          open: true,
          severity: 'error',
          message,
        })
      })
      .catch((e) => {
        setLoading(false)
        let message = e?.response?.data?.message || e?.message || 'Failed to create poll.'
        try {
          const parsed = JSON.parse(message)
          message = parsed?.error?.message || parsed?.message || message
        } catch {
          // keep original
        }
        onAlert?.({
          open: true,
          severity: 'error',
          message,
        })
        err(e)
      })
  }

  const formatAiError = (message?: string) => {
    if (!message) return `You've exceeded your AI budget. Please try again.`
    let raw = message
    try {
      const parsed = JSON.parse(message)
      raw = parsed?.error?.message || message
      if (parsed?.error?.status === 'UNAVAILABLE' || parsed?.error?.code === 503) {
        return 'AI is currently busy. Please try again in a moment.'
      }
    } catch {
      // ignore
    }
    if (/high demand|try again later|unavailable/i.test(raw)) {
      return 'AI is currently busy. Please try again in a moment.'
    }
    const retryMatch = raw.match(/retry in ([0-9.]+)s/i)
    const retryDelayMatch = raw.match(/retryDelay\\":\\"([0-9]+)s/i)
    const seconds = retryMatch?.[1] || retryDelayMatch?.[1]
    if (/cannot be created/i.test(raw)) {
      return 'This list cannot be created.'
    }
    if (/quota|RESOURCE_EXHAUSTED|rate/i.test(raw)) {
      return `You've exceeded your AI budget. Try again${seconds ? ` in ${Math.round(Number(seconds))}s` : ''}.`
    }
    return raw || 'Could not generate items. Please try again.'
  }

  const handleGenerate = () => {
    if (!prompt || isBusy) return
    sounds?.click?.()
    setAiLoading(true)
    setSuggestTrivia(false)
    setTriviaSuggestionDismissed(false)
    createTopic(prompt, generateOptionsCount, 'poll')
      .then((res) => {
        if (!res.success || !res.data?.items?.length) {
          onAlert?.({
            open: true,
            severity: 'error',
            message: formatAiError(res.message),
          })
          return
        }
        if (res.data.prompt) {
          setCreateMethod('prompt')
          setPrompt(res.data.prompt)
        }
        const requiresPrivate =
          Boolean(res.data.restricted) ||
          res.data.moderationStatus === 'offensive' ||
          res.data.moderationStatus === 'blocked'
        setForcedPrivateByModeration(requiresPrivate)
        if (requiresPrivate) {
          setVisibility('private')
          setPollResultsVisibility('hidden')
        }
        setSuggestTrivia(Boolean(res.data.suggestTrivia))
        if (res.data.category) {
          setPollCategories(split(res.data.category, ',').slice(0, 3))
        }
        if (res.data.tags) {
          setPollTags(split(res.data.tags, ',').slice(0, 3))
        }
        const targetCount = Math.min(
          MAX_AI_POLL_OPTIONS,
          Math.max(MIN_POLL_OPTIONS, generateOptionsCount)
        )
        const next = res.data.items.map((item) => item.title).slice(0, targetCount)
        while (next.length < targetCount) next.push('')
        setItems(next)
        setGenerateOptionsCount(toOptionChoice(targetCount))
        setAnimateItemsIntro(true)
        setItemsAnimKey((prev) => prev + 1)
      })
      .catch((e) => {
        err(e)
        onAlert?.({
          open: true,
          severity: 'error',
          message: formatAiError(e?.message),
        })
      })
      .finally(() => setAiLoading(false))
  }

  // const handleAddItem = () => {
  //   sounds?.click?.()
  //   if (items.length >= 10) return
  //   setItems((prev) => prev.concat(''))
  // }

  const handleChangeItem = (idx: number, value: string) => {
    setItems((prev) => prev.map((item, i) => (i === idx ? value : item)))
  }

  // const handleRemoveItem = (idx: number) => {
  //   sounds?.click?.()
  //   setItems((prev) => prev.filter((_, i) => i !== idx))
  // }

  useEffect(() => {
    if (!pollCategoryOptions.length) {
      getPollCategories()
        .then((res) => {
          if (res.success && res.data?.length) {
            setPollCategoryOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
    if (!pollTagOptions.length) {
      getPollTags()
        .then((res) => {
          if (res.success && res.data?.length) {
            setPollTagOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
  }, [pollCategoryOptions.length, pollTagOptions.length])

  useEffect(() => {
    if (!itemsAnimKey || !animateItemsIntro) return
    if (!sounds?.swipe) return
    if (!items.length) return
    const timers = items.map((_, idx) => window.setTimeout(() => sounds.swipe?.(), idx * 120))
    return () => {
      timers.forEach((id) => window.clearTimeout(id))
    }
  }, [itemsAnimKey, items.length, sounds, animateItemsIntro])

  useEffect(() => {
    if (!initialDraft) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSuggestTrivia(false)
    setTriviaSuggestionDismissed(false)
    setPrompt(initialDraft.prompt || '')
    const nextItems = (initialDraft.items || []).slice(0, MAX_POLL_OPTIONS)
    while (nextItems.length < MIN_POLL_OPTIONS) {
      nextItems.push('')
    }
    const targetCount = toOptionChoice(nextItems.length)
    const normalizedItems = nextItems.slice(0, targetCount)
    while (normalizedItems.length < targetCount) normalizedItems.push('')
    setItems(normalizedItems)
    setGenerateOptionsCount(targetCount)
    setPollCategories((initialDraft.categories || []).slice(0, 3))
    setPollTags((initialDraft.tags || []).slice(0, 3))
    setVisibility(initialDraft.visibility || 'public')
    setPollResultsVisibility(
      (initialDraft.visibility || 'public') === 'private' ? 'hidden' : 'visible'
    )
    setForcedPrivateByModeration(false)
    setAnimateItemsIntro(true)
    setItemsAnimKey((prev) => prev + 1)
  }, [initialDraft])

  return (
    <PollDesign>
      <Box py={2}>
        <EditorGrid
          className="editor-poll editor-with-format editor-create"
          sx={{
            '& .editor-title': {
              background: '#ffffff',
              borderColor: '#efddc9',
              boxShadow: '0 3px 0 #efddc9',
            },
            '& .MuiInputLabel-root, & .MuiFormHelperText-root': { color: '#855c3d' },
            '& .MuiInputLabel-root.Mui-disabled': { color: '#987457' },
            '& .MuiSelect-icon, & .MuiAutocomplete-popupIndicator': { color: '#944217' },
            '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-disabled)': {
              background: '#ffffff',
            },
            '&.editor-create .editor-meta > .MuiAccordion-root': {
              borderColor: '#efddc9',
              boxShadow: '0 3px 0 #efddc9',
            },
          }}
        >
          <Box className="editor-title">
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <EditorSectionHeading
                    step="01"
                    title="Title / prompt"
                    description="What should the community rank or answer?"
                    poll
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box flexGrow={1}>
                      <TextField
                        fullWidth
                        // label="Prompt"
                        placeholder={
                          isQuestion
                            ? 'If you had a superpower for one day, what would you pick?'
                            : 'Auto-generate poll options'
                        }
                        value={prompt}
                        disabled={isBusy}
                        onChange={(e) => {
                          setCreateMethod('manual')
                          setPrompt(e.target.value)
                        }}
                        InputProps={{ sx: inputSx }}
                        onKeyDown={(e) => {
                          if (!isQuestion && e.key === 'Enter') {
                            handleGenerate()
                          }
                        }}
                      />
                    </Box>
                    {!isQuestion && (
                      <Tooltip title="Use AI to fill options" arrow placement="top">
                        <span>
                          <GlassButton
                            variant="contained"
                            aria-label="Generate"
                            onClick={handleGenerate}
                            disabled={isBusy || !prompt}
                            size="large"
                            sx={{
                              fontSize: 18,
                              height: '56px',
                            }}
                          >
                            {isBusy ? (
                              <CircularProgress
                                size="30px"
                                sx={{
                                  color: '#944217',
                                  opacity: 0.225,
                                }}
                              />
                            ) : (
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <AutoAwesomeIcon fontSize="small" />
                                <Typography
                                  fontWeight={800}
                                  fontSize={13}
                                  sx={{ display: { xs: 'none', sm: 'block' } }}
                                >
                                  Generate
                                </Typography>
                              </Box>
                            )}
                          </GlassButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box
            className="editor-format"
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: '24px',
              background: '#ffffff',
              border: '1px solid #efddc9',
              boxShadow: '0 3px 0 #efddc9',
              '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-disabled)': {
                background: '#ffffff',
              },
            }}
          >
            {showHeader ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Create a Poll
                  </Typography>
                  <Typography variant="body2" color="#64748b" mt={0.5}>
                    {isQuestion
                      ? 'Ask a question, collect answers, then reveal the community results.'
                      : 'Collect rankings from the community, then open results for play.'}
                  </Typography>
                </Box>
                {onBack ? (
                  <GlassOutlineButton variant="text" onClick={onBack}>
                    Back
                  </GlassOutlineButton>
                ) : null}
              </Stack>
            ) : null}
            <Typography
              variant="h3"
              id="poll-answer-format"
              sx={{ fontSize: 22, color: '#693413', mb: 0.5, textAlign: 'left' }}
            >
              Answer format
            </Typography>
            <Typography variant="body2" sx={{ color: '#855c3d', mb: 2, textAlign: 'left' }}>
              Choose how people take part.
            </Typography>
            <RadioGroup
              aria-labelledby="poll-answer-format"
              name="poll-answer-format"
              value={pollFormat}
              onChange={(event) => {
                sounds?.click?.()
                setPollFormat(event.target.value as typeof pollFormat)
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              {[
                {
                  value: 'ranked_options',
                  title: 'Rank Options',
                  description: 'Give people options to put in order.',
                  icon: <FormatListNumberedRoundedIcon />,
                },
                {
                  value: 'community_answers',
                  title: 'Community Answers',
                  description: 'Ask a question. People add their own answers.',
                  icon: <ChatBubbleOutlineRoundedIcon />,
                },
              ].map((format) => (
                <FormControlLabel
                  key={format.value}
                  value={format.value}
                  disabled={isBusy}
                  control={
                    <Radio
                      size="small"
                      color="secondary"
                      inputProps={{ 'aria-label': format.title }}
                      sx={{ alignSelf: 'flex-start', mt: 0.25, p: 0.5, mr: 1, color: '#c6a385' }}
                    />
                  }
                  label={
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                        mb={0.5}
                        sx={{ color: '#944217' }}
                      >
                        {format.icon}
                        <Typography sx={{ fontWeight: 700, fontSize: 15, textAlign: 'left' }}>
                          {format.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: '#855c3d', textAlign: 'left', lineHeight: 1.5 }}
                      >
                        {format.description}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    m: 0,
                    p: 1.5,
                    alignItems: 'flex-start',
                    borderRadius: '18px',
                    border: `1.5px solid ${pollFormat === format.value ? '#e8bb99' : '#efddc9'}`,
                    background: pollFormat === format.value ? '#ffefdd' : '#fffaf5',
                    boxShadow: pollFormat === format.value ? '0 3px 0 #e8bb99' : '0 2px 0 #efddc9',
                    transition:
                      'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
                    opacity: isBusy ? 0.55 : 1,
                    '& .MuiFormControlLabel-label': { minWidth: 0 },
                    '&:has(input:focus-visible)': { outline: '2px solid #944217', outlineOffset: 3 },
                    '@media (hover: hover) and (pointer: fine)': {
                      '&:hover': isBusy ? {} : { borderColor: '#d78750' },
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
          <Box className="editor-main">
            {!isQuestion && (
              <GlassCard
                sx={{
                  py: { xs: 2, sm: 2.5 },
                  background: '#ffffff',
                  borderColor: '#efddc9',
                  boxShadow: '0 3px 0 #efddc9',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  px={{ xs: 2, sm: 2.5 }}
                  gap={1}
                  flexWrap="wrap"
                >
                  <EditorSectionHeading step="02" title="Options" poll />
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      label="Options"
                      select
                      size="small"
                      value={generateOptionsCount}
                      onChange={(e) => {
                        const parsed = Number.parseInt(e.target.value ?? '', 10)
                        if (!Number.isFinite(parsed)) return
                        sounds?.click?.()
                        setItemsLength(parsed)
                      }}
                      disabled={isBusy}
                      SelectProps={{ MenuProps: { PaperProps: { sx: pollMenuPaperSx } } }}
                      sx={{ width: 120 }}
                      InputProps={{
                        sx: {
                          ...inputSx,
                          height: 38,
                        },
                      }}
                    >
                      {POLL_OPTION_CHOICES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {/* {itemsPreview !== 10 ? (
              <Typography variant="caption" color={helperTextColor}>
                10 items required to create a poll.
              </Typography>
            ) : null} */}
                </Stack>
                <Stack spacing={1} px={{ xs: 1, sm: 2.5 }}>
                  {aiLoading ? (
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={`poll-items-${itemsAnimKey}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                      >
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                          <CircularProgress size={40} sx={{ color: '#e98549' }} />
                        </Box>
                      </motion.div>
                    </AnimatePresence>
                  ) : (
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={`poll-items-${itemsAnimKey}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                      >
                        {items?.length ? (
                          items.map((item, idx) => (
                            <motion.div
                              key={`poll-item-${idx}`}
                              initial={animateItemsIntro ? { opacity: 0, y: 12 } : false}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: animateItemsIntro ? idx * 0.12 : 0,
                                duration: 0.28,
                                ease: 'easeOut',
                              }}
                            >
                              <Box display="flex" gap={0} alignItems="stretch" mb={1}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={`Option ${idx + 1}`}
                                  value={item}
                                  onChange={(e) => handleChangeItem(idx, e.target.value)}
                                  disabled={isBusy}
                                  InputProps={{
                                    sx: {
                                      ...inputSx,
                                    },
                                  }}
                                />
                              </Box>
                            </motion.div>
                          ))
                        ) : (
                          <Box></Box>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  )}
                </Stack>

                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="flex-start"
                  px={{ xs: 1, sm: 3 }}
                  flexDirection="column"
                  gap={1}
                >
                  {items?.length > 10 ? (
                    <Typography variant="caption" color="#855c3d">
                      Only top 10 voted items will be used during gameplay.
                    </Typography>
                  ) : null}
                </Box>
              </GlassCard>
            )}
            {isQuestion && (
              <GlassCard
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  background: '#ffffff',
                  border: '1px solid #efddc9',
                  boxShadow: '0 3px 0 #efddc9',
                }}
              >
                <Box display="flex" gap={1.5} alignItems="center" mb={1.5}>
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      background: '#ffefdd',
                      color: '#944217',
                      borderRadius: '14px',
                      boxShadow: '0 3px 0 #e8bb99',
                    }}
                  >
                    <ChatBubbleOutlineRoundedIcon />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{ fontSize: 24, color: '#693413', textAlign: 'left' }}
                  >
                    Community Answers
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#855c3d', textAlign: 'left', mb: 2 }}>
                  People submit one answer each. Close submissions when you are ready to reveal
                  results and play.
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '16px',
                    background: '#fffaf5',
                    border: '1px solid #efddc9',
                  }}
                >
                  <FormControlLabel
                    labelPlacement="start"
                    sx={{
                      m: 0,
                      width: '100%',
                      justifyContent: 'space-between',
                      gap: 1,
                      '& .MuiFormControlLabel-label': {
                        color: '#693413',
                        fontWeight: 600,
                        textAlign: 'left',
                      },
                    }}
                    control={
                      <Switch
                        checked={!showSubmittedAnswers}
                        disabled={isBusy}
                        onChange={(_event, checked) => {
                          sounds?.click?.()
                          setShowSubmittedAnswers(!checked)
                        }}
                        color="secondary"
                      />
                    }
                    label="Hide answers until submissions close"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ color: '#855c3d', textAlign: 'left', mt: 0.5 }}
                  >
                    {showSubmittedAnswers
                      ? 'People can read answers and choose an existing answer. The community ranking updates as responses arrive.'
                      : 'People only see their own answer until submissions close. Hidden public questions cannot be played while open.'}
                  </Typography>
                </Box>
              </GlassCard>
            )}
          </Box>
          <Box className="editor-meta">
            <DiscoverabilityAccordion
              autoExpandKey={
                pollCategories.length || pollTags.length
                  ? [itemsAnimKey, ...pollCategories, ...pollTags].join('|')
                  : ''
              }
              onToggle={sounds?.click}
              poll
            >
              <Stack spacing={1}>
                <Stack spacing={1}>
                  <Autocomplete
                    size="small"
                    value={pollCategories}
                    onChange={(_e, value) => {
                      setPollCategories(normalizeMetaList(value))
                    }}
                    multiple={true}
                    freeSolo={true}
                    disabled={isBusy}
                    filterOptions={(options, params) => {
                      if (pollCategories.length >= 3) return []
                      const filtered = filterOption(options, params)
                      const inputValue = params.inputValue?.trim().slice(0, 16)
                      if (
                        inputValue &&
                        !pollCategories.some(
                          (value) => value.toLowerCase() === inputValue.toLowerCase()
                        )
                      ) {
                        filtered.push(inputValue)
                      }
                      return filtered
                    }}
                    options={mergedPollCategoryOptions}
                    componentsProps={{ paper: { sx: pollMenuPaperSx } }}
                    renderInput={({ InputProps, ...params }) => (
                      <TextField
                        InputProps={{
                          ...InputProps,
                          sx: inputSx,
                        }}
                        {...params}
                        placeholder={pollCategories.length >= 3 ? 'Limit reached' : 'Categories'}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    )}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({
                          index,
                        })
                        return (
                          <Chip
                            variant="filled"
                            size="small"
                            label={normalizeMetaValue(option)}
                            key={key}
                            deleteIcon={
                              <ClearRoundedIcon
                                fontSize="inherit"
                                sx={{ fontSize: '13px !important' }}
                              />
                            }
                            sx={{
                              background: altColor,
                              color: altColorDarkText,
                              fontWeight: 600,
                              boxShadow: shadow1,
                            }}
                            {...tagProps}
                          />
                        )
                      })
                    }
                  />
                  <Autocomplete
                    size="small"
                    value={pollTags}
                    onChange={(_e, value) => {
                      setPollTags(normalizeMetaList(value))
                    }}
                    multiple={true}
                    freeSolo
                    disabled={isBusy}
                    filterOptions={(options, params) => {
                      if (pollTags.length >= 3) return []
                      const filtered = filterOption(options, params)
                      const inputValue = params.inputValue?.trim().slice(0, 16)
                      if (
                        inputValue &&
                        !pollTags.some((value) => value.toLowerCase() === inputValue.toLowerCase())
                      ) {
                        filtered.push(inputValue)
                      }
                      return filtered
                    }}
                    options={mergedPollTagOptions}
                    componentsProps={{ paper: { sx: pollMenuPaperSx } }}
                    renderInput={({ InputProps, ...params }) => (
                      <TextField
                        InputProps={{
                          ...InputProps,
                          sx: inputSx,
                        }}
                        {...params}
                        placeholder={pollTags.length >= 3 ? 'Limit reached' : 'Tags'}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    )}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({
                          index,
                        })
                        return (
                          <Chip
                            variant="filled"
                            size="small"
                            label={normalizeMetaValue(option)}
                            key={key}
                            deleteIcon={
                              <ClearRoundedIcon
                                fontSize="inherit"
                                sx={{ fontSize: '13px !important' }}
                              />
                            }
                            sx={{
                              background: altColor,
                              color: altColorDarkText,
                              fontWeight: 600,
                              boxShadow: shadow1,
                            }}
                            {...tagProps}
                          />
                        )
                      })
                    }
                  />
                  <Stack spacing={0}>
                    <TextField
                      select
                      fullWidth
                      label="Visibility"
                      SelectProps={{ MenuProps: { PaperProps: { sx: pollMenuPaperSx } } }}
                      value={visibility}
                      onChange={(e) => {
                        const newVisibility = e.target.value as PollVisibility
                        if (newVisibility === 'public') {
                          setPollResultsVisibility('visible')
                        }
                        setVisibility(newVisibility)
                      }}
                      disabled={isBusy || forcedPrivateByModeration}
                      InputProps={{ sx: inputSx }}
                      size="small"
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                    </TextField>
                    <Typography variant="caption" color="#855c3d" textAlign="left" pl={1}>
                      {forcedPrivateByModeration
                        ? "This list can't be set to public."
                        : visibility === 'public'
                          ? 'Visible and playable by the community.'
                          : 'Private and only shareable by link.'}
                    </Typography>
                  </Stack>
                  {!isQuestion && (
                    <Stack spacing={0}>
                      <TextField
                        select
                        fullWidth
                        label="Poll Results"
                        SelectProps={{ MenuProps: { PaperProps: { sx: pollMenuPaperSx } } }}
                        value={pollResultsVisibility}
                        onChange={(e) =>
                          setPollResultsVisibility(e.target.value as 'hidden' | 'visible')
                        }
                        disabled={isBusy || visibility === 'public'}
                        InputProps={{ sx: inputSx }}
                        size="small"
                      >
                        <MenuItem value="hidden">Hidden</MenuItem>
                        <MenuItem value="visible">Visible</MenuItem>
                      </TextField>
                      <Typography variant="caption" color="#855c3d" textAlign="left" pl={1}>
                        {visibility === 'public'
                          ? 'Public polls lock results mode. Switch to Private to change this.'
                          : pollResultsVisibility === 'hidden'
                            ? 'Results stay hidden until you switch to visible.'
                            : 'People can view community results after voting.'}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </DiscoverabilityAccordion>
          </Box>
          <Box className="editor-notice">
            {!isQuestion && suggestTrivia && !triviaSuggestionDismissed ? (
              <GlassCard
                sx={{
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1.5, sm: 2 },
                  background: '#ffffff',
                  border: `1px solid ${mainColor}`,
                }}
              >
                <Stack spacing={0.75}>
                  <Typography variant="h6" fontWeight={600} color={mainColorDarkText}>
                    Create Trivia instead?
                  </Typography>
                  <Typography variant="body2" color={helperTextColor}>
                    This topic looks objective and may work better as trivia, where ranking is fixed
                    by facts.
                  </Typography>
                  <Box display="flex" justifyContent="space-between" gap={1} pt={0.5}>
                    <Button
                      variant="text"
                      onClick={() => {
                        sounds?.click?.()
                        setTriviaSuggestionDismissed(true)
                      }}
                      color="error"
                      sx={{ borderRadius: '12px', px: 2 }}
                    >
                      Ignore
                    </Button>
                    <GlassButton
                      variant="contained"
                      onClick={() => {
                        sounds?.click?.()
                        onConvertToTrivia?.({
                          prompt,
                          items: items
                            .map((i) => i.trim())
                            .filter(Boolean)
                            .slice(0, 10),
                          categories: pollCategories.slice(0, 3),
                          tags: pollTags.slice(0, 3),
                          visibility: visibility === 'private' ? 'private' : 'public',
                        })
                      }}
                    >
                      Convert to Trivia
                    </GlassButton>
                  </Box>
                </Stack>
              </GlassCard>
            ) : null}
          </Box>
          <Box className="editor-actions">
            <Stack spacing={1}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  width: {
                    xs: '100%',
                    sm: 'inherit',
                    md: 'inherit',
                    lg: 'inherit',
                  },
                }}
              >
                <Box
                  px={{
                    xs: 1,
                    sm: 0,
                  }}
                  width={{
                    xs: '100%',
                    sm: 'inherit',
                    md: 'inherit',
                    lg: 'inherit',
                  }}
                >
                  <GlassButton
                    variant="contained"
                    disabled={isBusy || !prompt || !hasValidItemCount}
                    onClick={handleCreate}
                    sx={{
                      pl: 4,
                      pr: 3,
                      py: 1,
                      fontWeight: 700,
                      minWidth: '250px',
                      height: '56px',
                      // background: 'linear-gradient(135deg, #FFAC48 0%, #F39A2A 60%, #eb8509 100%)',
                      // boxShadow: '0 12px 28px rgba(235, 133, 9, 0.28)',
                      width: {
                        xs: '100%',
                        sm: 'inherit',
                        md: 'inherit',
                        lg: 'inherit',
                      },
                    }}
                  >
                    <Box display="flex" gap={1.2} justifyContent="center" alignItems="center">
                      {loading ? (
                        <CircularProgress size={22} sx={{ color: '#944217' }} />
                      ) : (
                        <>
                          <Typography
                            fontWeight={700}
                            fontSize={22}
                            sx={{
                              mb: '2px',
                              color: 'inherit',
                            }}
                          >
                            Create
                          </Typography>
                          <SvgIcon sx={{ color: 'inherit' }}>
                            <DrawRoundedIcon />
                          </SvgIcon>
                        </>
                      )}
                    </Box>
                  </GlassButton>
                </Box>
              </Box>

              {!hasValidItemCount ? (
                <Box display="flex" alignItems="center" justifyContent="flex-end" px={1}>
                  <Typography variant="body2" color={helperTextColor}>
                    Need at least 10 options
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </EditorGrid>

        {/* <GlassPanel
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(214,244,255,0.5) 100%)',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Options
          </Typography>
          <GlassOutlineButton
            variant="text"
            onClick={() => setOptionsOpen((v) => !v)}
            disabled={isBusy}
            sx={{
              fontWeight: 500,
              color: mainColorDarker,
              borderColor: mainColorLighter,
            }}
          >
            {optionsOpen ? 'Hide' : 'Show'}
          </GlassOutlineButton>
        </Stack>
        <Collapse in={optionsOpen}>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="#64748b" mb={0.5}>
                Results Open
              </Typography>
              <RadioGroup
                row
                value={resultsMode}
                onChange={(e) => setResultsMode(e.target.value as 'open' | 'at' | 'when')}
                disabled={isBusy}
              >
                <FormControlLabel value="open" control={<Radio />} label="Always" />
                <FormControlLabel value="at" control={<Radio />} label="At time" />
                <FormControlLabel value="when" control={<Radio />} label="Responses" />
              </RadioGroup>
            </Grid>
            {resultsMode === 'at' ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Results open at"
                  type="datetime-local"
                  value={closeAt}
                  onChange={(e) => setCloseAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={isBusy}
                  InputProps={{ sx: inputSx }}
                />
              </Grid>
            ) : null}
            {resultsMode === 'when' ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Results open when # responses"
                  value={closeOnCount}
                  onChange={(e) => setCloseOnCount(e.target.value)}
                  disabled={isBusy}
                  InputProps={{ sx: inputSx }}
                  type="number"
                />
              </Grid>
            ) : null}
          </Grid>
        </Collapse>
      </GlassPanel> */}
      </Box>
    </PollDesign>
  )
}
